import { SWRConfig, mutate } from 'swr';
import type { ReactNode } from 'react';
import useSWR from 'swr';
import type { DCFilters, DixonColesResponse, DCBookmakersResponse, TeamRollupOut, DCFilterPreset, DCFilterPresetsResponse } from './types';

interface SwrProviderProps {
  children: ReactNode;
}

// Type exports for referee hooks
export type RefereeScopeKey = 'season' | 'last10' | 'last5';
export type RefereeSortKey = 'matches' | 'yellows_per_90' | 'reds_per_90' | 'fouls_per_90' | 'strictness' | 'fouls_per_card';

// Token getter for authenticated requests
let authTokenGetter: (() => Promise<string | null>) | null = null;

export function setSwrAuthTokenGetter(getter: () => Promise<string | null>) {
  authTokenGetter = getter;
}

// Check if auth token getter is configured
export function isSwrAuthReady(): boolean {
  return authTokenGetter !== null;
}

// Base API URL
export const API_BASE = (import.meta as any).env?.VITE_API_BASE ?? 'http://localhost:8005/v1';

// Default fetcher function with auth support
export async function fetcher(url: string): Promise<any> {
  const token = authTokenGetter ? await authTokenGetter() : null;
  const headers: Record<string, string> = {};
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const fullUrl = url.startsWith('http') ? url : `${API_BASE}${url}`;
  const res = await fetch(fullUrl, { headers });

  if (!res.ok) throw new Error(`API error ${res.status}`);
  return res.json();
}

// SWR configuration object for use in App.tsx
export const swrConfig = {
  revalidateOnFocus: false,
  revalidateOnReconnect: true,
  dedupingInterval: 2000,
  errorRetryCount: 3,
  errorRetryInterval: 5000,
  shouldRetryOnError: true,
  focusThrottleInterval: 5000,
  fetcher,
};

// Force revalidate odds
export function forceRevalidateOdds(fixtureId?: number) {
  if (fixtureId) {
    mutate(`/odds/fixtures/${fixtureId}`);
    mutate(`/odds/fixtures/${fixtureId}/preferred`);
  } else {
    mutate((key) => typeof key === 'string' && key.includes('/odds/'));
  }
}

// Value bets hook
export function useValueBets(params?: {
  minOdds?: number | null
  maxOdds?: number | null
  minEdge?: number | null
  maxEdge?: number | null
  marketCategories?: string[] | null
  leagueIds?: number[] | null
  fixtureId?: number | null
  sortBy?: string
  sortOrder?: string
  limit?: number
  offset?: number
  daysAhead?: number
}) {
  // Build query string from params
  const queryParams = new URLSearchParams()
  if (params) {
    if (params.minOdds != null) queryParams.append('min_odds', params.minOdds.toString())
    if (params.maxOdds != null) queryParams.append('max_odds', params.maxOdds.toString())
    if (params.minEdge != null) queryParams.append('min_edge', params.minEdge.toString())
    if (params.maxEdge != null) queryParams.append('max_edge', params.maxEdge.toString())
    if (params.marketCategories?.length) queryParams.append('market_categories', params.marketCategories.join(','))
    if (params.leagueIds?.length) queryParams.append('league_ids', params.leagueIds.join(','))
    if (params.fixtureId != null) queryParams.append('fixture_id', params.fixtureId.toString())
    if (params.sortBy) queryParams.append('sort_by', params.sortBy)
    if (params.sortOrder) queryParams.append('sort_order', params.sortOrder)
    if (params.limit != null) queryParams.append('limit', params.limit.toString())
    if (params.offset != null) queryParams.append('offset', params.offset.toString())
    if (params.daysAhead != null) queryParams.append('days_ahead', params.daysAhead.toString())
  }

  const queryString = queryParams.toString()
  const url = queryString ? `/fixtures/value-bets?${queryString}` : '/fixtures/value-bets'

  const { data, error, mutate: revalidate } = useSWR(url, fetcher, {
    refreshInterval: 300000, // Refresh every 5 minutes
    revalidateOnFocus: false,
  });

  return {
    data: data || { items: [], total_count: 0 },
    isLoading: !error && !data,
    isError: error,
    revalidate,
  };
}

// Live fixture counts hook
export function useLiveFixtureCounts(leagueIds?: number[]) {
  const params = leagueIds && leagueIds.length > 0
    ? `?league_ids=${leagueIds.join(',')}`
    : '';

  const { data, error } = useSWR(
    `/fixtures/live-counts${params}`,
    fetcher,
    {
      refreshInterval: 30000, // Refresh every 30 seconds
      revalidateOnFocus: true,
    }
  );

  return {
    data: data || { live: 0, upcoming_today: 0 },
    isLoading: !error && !data,
    isError: error,
  };
}

// Today's fixture count (all leagues) for Live Today feature
export function useTodayFixtureCount() {
  const { data, error } = useSWR(
    '/fixtures/live-counts',
    fetcher,
    {
      refreshInterval: 60000, // Refresh every minute
      revalidateOnFocus: true,
    }
  );

  // Sum of live + upcoming today
  const totalToday = data ? (data.live || 0) + (data.upcoming_today || 0) : 0;

  return {
    data: totalToday,
    live: data?.live || 0,
    upcomingToday: data?.upcoming_today || 0,
    isLoading: !error && !data,
    isError: error,
  };
}

// Upcoming fixtures hook
export function useUpcomingFixtures(leagueId?: number, days: number = 7) {
  const params = leagueId ? `?league_id=${leagueId}&days=${days}` : `?days=${days}`;

  const { data, error, mutate: revalidate } = useSWR(
    `/fixtures/upcoming${params}`,
    fetcher,
    {
      refreshInterval: 300000, // Refresh every 5 minutes
      revalidateOnFocus: false,
    }
  );

  return {
    fixtures: data?.fixtures || [],
    isLoading: !error && !data,
    isError: error,
    revalidate,
  };
}

// Live tips hook
export function useLiveTips(params?: { league_ids?: number[]; market_category?: string; min_probability?: number }) {
  const searchParams = new URLSearchParams();
  if (params?.league_ids?.length) searchParams.set('league_ids', params.league_ids.join(','));
  if (params?.market_category) searchParams.set('market_category', params.market_category);
  if (params?.min_probability) searchParams.set('min_probability', String(params.min_probability));
  const query = searchParams.toString();

  const { data, error, mutate: revalidate } = useSWR(
    `/tips${query ? `?${query}` : ''}`,
    fetcher,
    {
      refreshInterval: 60000, // Refresh every minute for live tips
      revalidateOnFocus: true,
    }
  );

  return {
    tips: data?.tips || [],
    isLoading: !error && !data,
    isError: error,
    revalidate,
  };
}

// Tip statistics hook
export function useTipStatistics() {
  const { data, error } = useSWR('/tips/statistics', fetcher, {
    refreshInterval: 300000, // Refresh every 5 minutes
    revalidateOnFocus: false,
  });

  return {
    statistics: data || { total_tips: 0, win_rate: 0, avg_odds: 0 },
    isLoading: !error && !data,
    isError: error,
  };
}

// Referees list hook
export function useReferees(params: {
  leagueId: number;
  scope?: RefereeScopeKey;
  sortBy?: RefereeSortKey;
  sortOrder?: 'asc' | 'desc';
}) {
  const searchParams = new URLSearchParams();
  searchParams.set('league_id', String(params.leagueId));
  if (params.scope) searchParams.set('scope', params.scope);
  if (params.sortBy) searchParams.set('sort_by', params.sortBy);
  if (params.sortOrder) searchParams.set('sort_order', params.sortOrder);
  const query = searchParams.toString();

  const { data, error, mutate: revalidate } = useSWR(
    `/referees?${query}`,
    fetcher,
    {
      refreshInterval: 3600000, // Refresh every hour
      revalidateOnFocus: false,
    }
  );

  return {
    data: data || null,
    error,
    isLoading: !error && !data,
    mutate: revalidate,
  };
}

// Referee detail hook
export function useRefereeDetail(
  refereeId: number,
  leagueId: number,
  scope?: RefereeScopeKey,
  seasonYear?: number | 'all' | null
) {
  const searchParams = new URLSearchParams();
  searchParams.set('league_id', String(leagueId));
  if (scope) searchParams.set('scope', scope);
  if (seasonYear === 'all') {
    searchParams.set('all', 'true');
  } else if (seasonYear) {
    searchParams.set('season_year', String(seasonYear));
  }
  const query = searchParams.toString();

  const { data, error, mutate: revalidate } = useSWR(
    refereeId ? `/referees/${refereeId}?${query}` : null,
    fetcher,
    {
      refreshInterval: 3600000, // Refresh every hour
      revalidateOnFocus: false,
    }
  );

  return {
    data,
    error,
    isLoading: !error && !data,
    mutate: revalidate,
  };
}

// Fixture hooks for main App.tsx
export function useFixtures(leagueId?: number, days: number = 10) {
  const params = leagueId ? `?league_id=${leagueId}&days=${days}` : `?days=${days}`;
  const { data, error, mutate: revalidate } = useSWR(
    `/fixtures/upcoming${params}`,
    fetcher,
    {
      refreshInterval: 60000, // Refresh every minute
      revalidateOnFocus: true,
    }
  );

  return {
    data: data || [],  // API returns array directly, not { fixtures: [] }
    error,
    mutate: revalidate,
  };
}

// Fetch a single fixture by ID (for direct navigation from dashboard widgets)
export function useFixtureLite(fixtureId: number | null) {
  const { data, error, isLoading } = useSWR(
    fixtureId ? `/fixtures/${fixtureId}` : null,
    fetcher,
    {
      revalidateOnFocus: false,
    }
  );

  return { data, error, isLoading };
}

export function useFixtureBuilder(fixtureId: number | null) {
  const { data, error } = useSWR(
    fixtureId ? `/fixtures/${fixtureId}/builder` : null,
    fetcher,
    {
      revalidateOnFocus: false,
    }
  );

  return { data, error };
}

export function useFixtureTeams(fixtureId: number | null) {
  const { data, error } = useSWR(
    fixtureId ? `/fixtures/${fixtureId}/teams` : null,
    fetcher,
    {
      revalidateOnFocus: false,
    }
  );

  return { data, error };
}

export function useFixtureSeasonStats(fixtureId: number | null) {
  const { data, error } = useSWR(
    fixtureId ? `/fixtures/${fixtureId}/season-stats` : null,
    fetcher,
    {
      revalidateOnFocus: false,
    }
  );

  return { data, error };
}

export function useFixtureHeadToHead(fixtureId: number | null) {
  const { data, error } = useSWR(
    fixtureId ? `/fixtures/${fixtureId}/head-to-head` : null,
    fetcher,
    {
      revalidateOnFocus: false,
    }
  );

  return { data, error };
}

export function useFixturePlayers(fixtureId: number | null) {
  const { data, error } = useSWR(
    fixtureId ? `/fixtures/${fixtureId}/players` : null,
    fetcher,
    {
      revalidateOnFocus: false,
    }
  );

  return { data, error };
}

export function useH2HStats(fixtureId: number | null, filter: string = 'last5') {
  const { data, error } = useSWR(
    fixtureId ? `/fixtures/${fixtureId}/h2h-stats?filter=${filter}` : null,
    fetcher,
    {
      revalidateOnFocus: false,
    }
  );

  return { data, error };
}

export function useFixtureTimeSplits(fixtureId: number | null, scope: string = 'last5') {
  const { data, error } = useSWR(
    fixtureId ? `/fixtures/${fixtureId}/time-splits?scope=${scope}` : null,
    fetcher,
    {
      revalidateOnFocus: false,
    }
  );

  return { data, error };
}

export function useFixtureCardsContext(fixtureId: number | null) {
  const { data, error } = useSWR(
    fixtureId ? `/fixtures/${fixtureId}/cards-context` : null,
    fetcher,
    {
      revalidateOnFocus: false,
    }
  );

  return { data, error };
}

// ============================================
// Cards Hub Hooks
// ============================================

// Cards Hub overview (multi-fixture)
export function useCardsOverview(params?: {
  date?: string | null;
  leagueId?: number | null;
  sort?: 'kickoff' | 'projection' | 'confidence';
  minConfidence?: number | null;
}) {
  const searchParams = new URLSearchParams();
  if (params?.date) searchParams.set('date', params.date);
  if (params?.leagueId) searchParams.set('league_id', String(params.leagueId));
  if (params?.sort) searchParams.set('sort', params.sort);
  if (params?.minConfidence != null) searchParams.set('min_confidence', String(params.minConfidence));
  const query = searchParams.toString();

  const { data, error, mutate: revalidate } = useSWR(
    `/cards/overview${query ? `?${query}` : ''}`,
    fetcher,
    {
      refreshInterval: 300000, // Refresh every 5 minutes
      revalidateOnFocus: false,
    }
  );

  return {
    data: data || null,
    error,
    isLoading: !error && !data,
    mutate: revalidate,
  };
}

// Cards Hub single fixture deep-dive
export function useCardsFixture(fixtureId: number | null) {
  const { data, error, mutate: revalidate } = useSWR(
    fixtureId ? `/cards/fixture/${fixtureId}` : null,
    fetcher,
    {
      refreshInterval: 300000, // Refresh every 5 minutes
      revalidateOnFocus: false,
    }
  );

  return {
    data: data || null,
    error,
    isLoading: !error && !data,
    mutate: revalidate,
  };
}

// Cards Hub saved fixtures
export function useCardsSavedFixtures(watchlistId?: number | null) {
  const searchParams = new URLSearchParams();
  if (watchlistId) searchParams.set('watchlist_id', String(watchlistId));
  const query = searchParams.toString();

  const { data, error, mutate: revalidate } = useSWR(
    `/cards/saved${query ? `?${query}` : ''}`,
    fetcher,
    {
      revalidateOnFocus: true,
    }
  );

  return {
    data: data || { saved_fixtures: [], total: 0 },
    error,
    isLoading: !error && !data,
    mutate: revalidate,
  };
}

// Cards Hub watchlists
export function useCardsWatchlists() {
  const { data, error, mutate: revalidate } = useSWR(
    '/cards/watchlists',
    fetcher,
    {
      revalidateOnFocus: true,
    }
  );

  return {
    data: data || { watchlists: [], total: 0 },
    error,
    isLoading: !error && !data,
    mutate: revalidate,
  };
}

// Cards Hub watchlist detail
export function useCardsWatchlistDetail(watchlistId: number | null) {
  const { data, error, mutate: revalidate } = useSWR(
    watchlistId ? `/cards/watchlists/${watchlistId}` : null,
    fetcher,
    {
      revalidateOnFocus: true,
    }
  );

  return {
    data: data || null,
    error,
    isLoading: !error && !data,
    mutate: revalidate,
  };
}

// Cards Hub mutation helpers (for save/unsave/watchlist operations)
export async function saveCardsFixture(fixtureId: number, watchlistId?: number, notes?: string) {
  const token = authTokenGetter ? await authTokenGetter() : null;
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const res = await fetch(`${API_BASE}/cards/saved`, {
    method: 'POST',
    headers,
    body: JSON.stringify({
      fixture_id: fixtureId,
      watchlist_id: watchlistId,
      notes,
    }),
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || `Failed to save fixture: ${res.status}`);
  }
  return res.json();
}

export async function unsaveCardsFixture(fixtureId: number) {
  const token = authTokenGetter ? await authTokenGetter() : null;
  const headers: Record<string, string> = {};
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const res = await fetch(`${API_BASE}/cards/saved/${fixtureId}`, {
    method: 'DELETE',
    headers,
  });

  if (!res.ok && res.status !== 204) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || `Failed to unsave fixture: ${res.status}`);
  }
  return true;
}

export async function createCardsWatchlist(name: string) {
  const token = authTokenGetter ? await authTokenGetter() : null;
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const res = await fetch(`${API_BASE}/cards/watchlists`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ name }),
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || `Failed to create watchlist: ${res.status}`);
  }
  return res.json();
}

export async function deleteCardsWatchlist(watchlistId: number) {
  const token = authTokenGetter ? await authTokenGetter() : null;
  const headers: Record<string, string> = {};
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const res = await fetch(`${API_BASE}/cards/watchlists/${watchlistId}`, {
    method: 'DELETE',
    headers,
  });

  if (!res.ok && res.status !== 204) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || `Failed to delete watchlist: ${res.status}`);
  }
  return true;
}

export async function addFixtureToWatchlist(watchlistId: number, fixtureId: number, notes?: string) {
  const token = authTokenGetter ? await authTokenGetter() : null;
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const res = await fetch(`${API_BASE}/cards/watchlists/${watchlistId}/fixtures`, {
    method: 'POST',
    headers,
    body: JSON.stringify({
      fixture_id: fixtureId,
      notes,
    }),
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || `Failed to add fixture to watchlist: ${res.status}`);
  }
  return res.json();
}

// ============================================
// Goals Hub Hooks
// ============================================

import type {
  GoalsOverviewResponse,
  GoalsFixtureDetailResponse,
  GoalsStreaksResponse,
  GoalsSavedFixture,
  GoalsWatchlist,
  BetStrategy,
  BetStrategyCreate,
  BetStrategyUpdate,
  StrategyListResponse,
  StrategyRunResponse,
  AlertCheckResponse,
  BulkAlertCheckResponse,
} from './types';

export function useGoalsOverview(params?: {
  date?: string | null;
  leagueId?: number | null;
  sort?: 'btts' | 'o25' | 'confidence' | 'kickoff';
  minBtts?: number | null;
  minO25?: number | null;
  minConfidence?: number | null;
} | null) {
  const searchParams = new URLSearchParams();
  if (params?.date) searchParams.set('date', params.date);
  if (params?.leagueId) searchParams.set('league_id', String(params.leagueId));
  if (params?.sort) searchParams.set('sort', params.sort);
  if (params?.minBtts != null) searchParams.set('min_btts', String(params.minBtts));
  if (params?.minO25 != null) searchParams.set('min_o25', String(params.minO25));
  if (params?.minConfidence != null) searchParams.set('min_confidence', String(params.minConfidence));
  const query = searchParams.toString();

  // Pass null as key to skip fetching when params is null/undefined
  const swrKey = params ? `/goals/overview${query ? `?${query}` : ''}` : null;

  const { data, error, mutate: revalidate } = useSWR<GoalsOverviewResponse>(
    swrKey,
    fetcher,
    {
      refreshInterval: 300000, // Refresh every 5 minutes
      revalidateOnFocus: false,
    }
  );

  return {
    data: data || null,
    error,
    isLoading: swrKey !== null && !error && !data,
    mutate: revalidate,
  };
}

export function useGoalsFixture(fixtureId: number | null) {
  const { data, error, mutate: revalidate } = useSWR<GoalsFixtureDetailResponse>(
    fixtureId ? `/goals/fixture/${fixtureId}` : null,
    fetcher,
    {
      refreshInterval: 300000,
      revalidateOnFocus: false,
    }
  );

  return {
    data: data || null,
    error,
    isLoading: fixtureId !== null && !error && !data,
    mutate: revalidate,
  };
}

export function useGoalsStreaks(params?: {
  date?: string | null;
  leagueId?: number | null;
  streakType?: string | null;
  minLength?: number | null;
}) {
  const searchParams = new URLSearchParams();
  if (params?.date) searchParams.set('date', params.date);
  if (params?.leagueId) searchParams.set('league_id', String(params.leagueId));
  if (params?.streakType) searchParams.set('streak_type', params.streakType);
  if (params?.minLength != null) searchParams.set('min_length', String(params.minLength));
  const query = searchParams.toString();

  const { data, error, mutate: revalidate } = useSWR<GoalsStreaksResponse>(
    `/goals/streaks${query ? `?${query}` : ''}`,
    fetcher,
    {
      refreshInterval: 300000,
      revalidateOnFocus: false,
    }
  );

  return {
    data: data || null,
    error,
    isLoading: !error && !data,
    mutate: revalidate,
  };
}

export function useGoalsSavedFixtures(watchlistId?: number | null) {
  const query = watchlistId ? `?watchlist_id=${watchlistId}` : '';

  const { data, error, mutate: revalidate } = useSWR<GoalsSavedFixture[]>(
    `/goals/saved${query}`,
    fetcher,
    {
      revalidateOnFocus: true,
    }
  );

  return {
    data: data || [],
    error,
    isLoading: !error && !data,
    mutate: revalidate,
  };
}

export function useGoalsWatchlists() {
  const { data, error, mutate: revalidate } = useSWR<GoalsWatchlist[]>(
    '/goals/watchlists',
    fetcher,
    {
      revalidateOnFocus: true,
    }
  );

  return {
    data: data || [],
    error,
    isLoading: !error && !data,
    mutate: revalidate,
  };
}

// Goals Hub mutation helpers
export async function saveGoalsFixture(
  fixtureId: number,
  watchlistId?: number | null,
  notes?: string | null,
  targetMarket?: string | null
) {
  const token = authTokenGetter ? await authTokenGetter() : null;
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const res = await fetch(`${API_BASE}/goals/saved`, {
    method: 'POST',
    headers,
    body: JSON.stringify({
      fixture_id: fixtureId,
      watchlist_id: watchlistId,
      notes,
      target_market: targetMarket,
    }),
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || `Failed to save fixture: ${res.status}`);
  }
  return res.json();
}

export async function unsaveGoalsFixture(fixtureId: number) {
  const token = authTokenGetter ? await authTokenGetter() : null;
  const headers: Record<string, string> = {};
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const res = await fetch(`${API_BASE}/goals/saved/${fixtureId}`, {
    method: 'DELETE',
    headers,
  });

  if (!res.ok && res.status !== 204) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || `Failed to unsave fixture: ${res.status}`);
  }
  return true;
}

export async function createGoalsWatchlist(name: string) {
  const token = authTokenGetter ? await authTokenGetter() : null;
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const res = await fetch(`${API_BASE}/goals/watchlists`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ name }),
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || `Failed to create watchlist: ${res.status}`);
  }
  return res.json();
}

export async function deleteGoalsWatchlist(watchlistId: number) {
  const token = authTokenGetter ? await authTokenGetter() : null;
  const headers: Record<string, string> = {};
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const res = await fetch(`${API_BASE}/goals/watchlists/${watchlistId}`, {
    method: 'DELETE',
    headers,
  });

  if (!res.ok && res.status !== 204) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || `Failed to delete watchlist: ${res.status}`);
  }
  return true;
}

// ============================================
// Player Stats Hooks
// ============================================

import type { PlayerSOTResponse, PlayerFoulsCardsResponse } from './types';

// Player SOT stats hook
export function usePlayerSOTStats(params?: {
  daysAhead?: number;
  leagueIds?: number[];
  fixtureId?: number | null;
  position?: string | null;
  minProbability?: number | null;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  limit?: number;
  offset?: number;
}) {
  const queryParams = new URLSearchParams();

  if (params?.daysAhead) queryParams.set('days_ahead', String(params.daysAhead));
  if (params?.leagueIds?.length) queryParams.set('league_ids', params.leagueIds.join(','));
  if (params?.fixtureId) queryParams.set('fixture_id', String(params.fixtureId));
  if (params?.position) queryParams.set('position', params.position);
  if (params?.minProbability != null) queryParams.set('min_probability', String(params.minProbability));
  if (params?.sortBy) queryParams.set('sort_by', params.sortBy);
  if (params?.sortOrder) queryParams.set('sort_order', params.sortOrder);
  if (params?.limit) queryParams.set('limit', String(params.limit));
  if (params?.offset) queryParams.set('offset', String(params.offset));

  const queryString = queryParams.toString();
  const url = `/player-stats/sot${queryString ? `?${queryString}` : ''}`;

  const { data, error, mutate: revalidate } = useSWR<PlayerSOTResponse>(url, fetcher, {
    refreshInterval: 300000, // 5 minutes
    revalidateOnFocus: false,
  });

  return {
    data: data || { items: [], total_count: 0 },
    error,
    isLoading: !error && !data,
    mutate: revalidate,
  };
}

// Player Fouls & Cards stats hook
export function usePlayerFoulsCardsStats(params?: {
  daysAhead?: number;
  leagueIds?: number[];
  fixtureId?: number | null;
  position?: string | null;
  minProbability?: number | null;
  sortBy?: 'fouls_committed' | 'fouls_drawn' | 'cards' | 'tackles' | 'kickoff' | 'ref_factor';
  sortOrder?: 'asc' | 'desc';
  limit?: number;
  offset?: number;
}) {
  const queryParams = new URLSearchParams();

  if (params?.daysAhead) queryParams.set('days_ahead', String(params.daysAhead));
  if (params?.leagueIds?.length) queryParams.set('league_ids', params.leagueIds.join(','));
  if (params?.fixtureId) queryParams.set('fixture_id', String(params.fixtureId));
  if (params?.position) queryParams.set('position', params.position);
  if (params?.minProbability != null) queryParams.set('min_probability', String(params.minProbability));
  if (params?.sortBy) queryParams.set('sort_by', params.sortBy);
  if (params?.sortOrder) queryParams.set('sort_order', params.sortOrder);
  if (params?.limit) queryParams.set('limit', String(params.limit));
  if (params?.offset) queryParams.set('offset', String(params.offset));

  const queryString = queryParams.toString();
  const url = `/player-stats/fouls-cards${queryString ? `?${queryString}` : ''}`;

  const { data, error, mutate: revalidate } = useSWR<PlayerFoulsCardsResponse>(url, fetcher, {
    refreshInterval: 300000, // 5 minutes
    revalidateOnFocus: false,
  });

  return {
    data: data || { items: [], total_count: 0 },
    error,
    isLoading: !error && !data,
    mutate: revalidate,
  };
}

// ============================================
// Bet Strategy Creator Hooks
// ============================================

// List all strategies for current user
export function useStrategies(shouldFetch: boolean = true) {
  // Custom fetcher that returns empty data on 401 (not logged in)
  const authAwareFetcher = async (url: string) => {
    try {
      return await fetcher(url);
    } catch (err: any) {
      // Return empty data if not authenticated
      if (err.message?.includes('401')) {
        return { strategies: [], total: 0 };
      }
      throw err;
    }
  };

  const { data, error, mutate: revalidate } = useSWR<StrategyListResponse>(
    shouldFetch ? '/strategies' : null,
    authAwareFetcher,
    {
      revalidateOnFocus: true,
    }
  );

  return {
    data: data || { strategies: [], total: 0 },
    error,
    isLoading: shouldFetch && !error && !data,
    mutate: revalidate,
  };
}

// Get single strategy by ID
export function useStrategy(strategyId: number | null) {
  const { data, error, mutate: revalidate } = useSWR<BetStrategy>(
    strategyId ? `/strategies/${strategyId}` : null,
    fetcher,
    {
      revalidateOnFocus: false,
    }
  );

  return {
    data: data || null,
    error,
    isLoading: strategyId !== null && !error && !data,
    mutate: revalidate,
  };
}

// Check alerts for all strategies on app open
export function useStrategyAlerts(shouldFetch: boolean = true) {
  const { data, error, mutate: revalidate } = useSWR<BulkAlertCheckResponse>(
    shouldFetch ? '/strategies/alerts/check-all' : null,
    fetcher,
    {
      revalidateOnFocus: true,
      dedupingInterval: 60000, // Only check once per minute
    }
  );

  return {
    data: data || null,
    error,
    isLoading: shouldFetch && !error && !data,
    mutate: revalidate,
  };
}

// Create a new strategy
export async function createStrategy(payload: BetStrategyCreate): Promise<BetStrategy> {
  const token = authTokenGetter ? await authTokenGetter() : null;
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const res = await fetch(`${API_BASE}/strategies`, {
    method: 'POST',
    headers,
    body: JSON.stringify(payload),
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || `Failed to create strategy: ${res.status}`);
  }
  return res.json();
}

// Update an existing strategy
export async function updateStrategy(
  strategyId: number,
  payload: BetStrategyUpdate
): Promise<BetStrategy> {
  const token = authTokenGetter ? await authTokenGetter() : null;
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const res = await fetch(`${API_BASE}/strategies/${strategyId}`, {
    method: 'PATCH',
    headers,
    body: JSON.stringify(payload),
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || `Failed to update strategy: ${res.status}`);
  }
  return res.json();
}

// Delete a strategy
export async function deleteStrategy(strategyId: number): Promise<void> {
  const token = authTokenGetter ? await authTokenGetter() : null;
  const headers: Record<string, string> = {};
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const res = await fetch(`${API_BASE}/strategies/${strategyId}`, {
    method: 'DELETE',
    headers,
  });

  if (!res.ok && res.status !== 204) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || `Failed to delete strategy: ${res.status}`);
  }
}

// Run a strategy
export async function runStrategy(
  strategyId: number,
  params?: { page?: number; per_page?: number; sort_by?: 'probability' | 'kickoff' }
): Promise<StrategyRunResponse> {
  const token = authTokenGetter ? await authTokenGetter() : null;
  const headers: Record<string, string> = {};
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const queryParams = new URLSearchParams();
  if (params?.page) queryParams.set('page', String(params.page));
  if (params?.per_page) queryParams.set('per_page', String(params.per_page));
  if (params?.sort_by) queryParams.set('sort_by', params.sort_by);
  const query = queryParams.toString();

  const res = await fetch(`${API_BASE}/strategies/${strategyId}/run${query ? `?${query}` : ''}`, {
    method: 'POST',
    headers,
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || `Failed to run strategy: ${res.status}`);
  }
  return res.json();
}

// Check alerts for a specific strategy
export async function checkStrategyAlerts(strategyId: number): Promise<AlertCheckResponse> {
  const token = authTokenGetter ? await authTokenGetter() : null;
  const headers: Record<string, string> = {};
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const res = await fetch(`${API_BASE}/strategies/${strategyId}/alerts`, {
    method: 'GET',
    headers,
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || `Failed to check alerts: ${res.status}`);
  }
  return res.json();
}

// ============================================
// Dixon-Coles Page Hooks
// ============================================

export function useDixonColesFixtures(filters: DCFilters) {
  const params = new URLSearchParams();
  params.set('days_ahead', filters.days_ahead.toString());

  if (filters.bookmaker_slug) {
    params.set('bookmaker_slug', filters.bookmaker_slug);
  }
  if (filters.min_home_prob !== undefined) {
    params.set('min_home_prob', filters.min_home_prob.toString());
  }
  if (filters.min_draw_prob !== undefined) {
    params.set('min_draw_prob', filters.min_draw_prob.toString());
  }
  if (filters.min_away_prob !== undefined) {
    params.set('min_away_prob', filters.min_away_prob.toString());
  }
  if (filters.min_over_1_5 !== undefined) {
    params.set('min_over_1_5', filters.min_over_1_5.toString());
  }
  if (filters.min_over_2_5 !== undefined) {
    params.set('min_over_2_5', filters.min_over_2_5.toString());
  }
  if (filters.min_over_3_5 !== undefined) {
    params.set('min_over_3_5', filters.min_over_3_5.toString());
  }
  if (filters.min_btts !== undefined) {
    params.set('min_btts', filters.min_btts.toString());
  }
  // Per-market odds filters
  if (filters.min_home_odds !== undefined) {
    params.set('min_home_odds', filters.min_home_odds.toString());
  }
  if (filters.max_home_odds !== undefined) {
    params.set('max_home_odds', filters.max_home_odds.toString());
  }
  if (filters.min_draw_odds !== undefined) {
    params.set('min_draw_odds', filters.min_draw_odds.toString());
  }
  if (filters.max_draw_odds !== undefined) {
    params.set('max_draw_odds', filters.max_draw_odds.toString());
  }
  if (filters.min_away_odds !== undefined) {
    params.set('min_away_odds', filters.min_away_odds.toString());
  }
  if (filters.max_away_odds !== undefined) {
    params.set('max_away_odds', filters.max_away_odds.toString());
  }
  if (filters.min_over_1_5_odds !== undefined) {
    params.set('min_over_1_5_odds', filters.min_over_1_5_odds.toString());
  }
  if (filters.max_over_1_5_odds !== undefined) {
    params.set('max_over_1_5_odds', filters.max_over_1_5_odds.toString());
  }
  if (filters.min_over_2_5_odds !== undefined) {
    params.set('min_over_2_5_odds', filters.min_over_2_5_odds.toString());
  }
  if (filters.max_over_2_5_odds !== undefined) {
    params.set('max_over_2_5_odds', filters.max_over_2_5_odds.toString());
  }
  if (filters.min_over_3_5_odds !== undefined) {
    params.set('min_over_3_5_odds', filters.min_over_3_5_odds.toString());
  }
  if (filters.max_over_3_5_odds !== undefined) {
    params.set('max_over_3_5_odds', filters.max_over_3_5_odds.toString());
  }
  if (filters.min_btts_odds !== undefined) {
    params.set('min_btts_odds', filters.min_btts_odds.toString());
  }
  if (filters.max_btts_odds !== undefined) {
    params.set('max_btts_odds', filters.max_btts_odds.toString());
  }
  // Legacy global odds (deprecated)
  if (filters.min_odds !== undefined) {
    params.set('min_odds', filters.min_odds.toString());
  }
  if (filters.max_odds !== undefined) {
    params.set('max_odds', filters.max_odds.toString());
  }
  if (filters.min_edge !== undefined) {
    params.set('min_edge', filters.min_edge.toString());
  }
  if (filters.league_ids && filters.league_ids.length > 0) {
    params.set('league_ids', filters.league_ids.join(','));
  }
  if (filters.sort_by) {
    params.set('sort_by', filters.sort_by);
  }
  if (filters.sort_order) {
    params.set('sort_order', filters.sort_order);
  }

  const url = `/dixon-coles/fixtures?${params.toString()}`;

  const { data, error, mutate: revalidate } = useSWR<DixonColesResponse>(
    url,
    fetcher,
    {
      refreshInterval: 300000, // 5 min refresh
      revalidateOnFocus: false,
    }
  );

  return {
    data,
    error,
    isLoading: !data && !error,
    mutate: revalidate,
  };
}

export function useDCBookmakers() {
  const { data, error, mutate: revalidate } = useSWR<DCBookmakersResponse>(
    '/dixon-coles/bookmakers',
    fetcher,
    {
      revalidateOnFocus: false,
      dedupingInterval: 60000, // Cache for 1 minute
    }
  );

  return {
    data,
    error,
    isLoading: !data && !error,
    mutate: revalidate,
  };
}

// Team stats for expanded row (reuses existing fixture teams endpoint)
export function useDCTeamStats(fixtureId: number | null) {
  const { data, error, mutate: revalidate } = useSWR<TeamRollupOut[]>(
    fixtureId ? `/fixtures/${fixtureId}/teams` : null,
    fetcher,
    { revalidateOnFocus: false }
  );

  return {
    data,
    error,
    isLoading: fixtureId !== null && !data && !error,
    mutate: revalidate,
  };
}

// Load saved DC filters for current user
export function useDCSavedFilters() {
  const { data, error, mutate: revalidate } = useSWR<{ filters: DCFilters | null }>(
    '/dixon-coles/filters',
    fetcher,
    {
      revalidateOnFocus: false,
      dedupingInterval: 60000, // Cache for 1 minute
    }
  );

  return {
    data: data?.filters || null,
    error,
    isLoading: !data && !error,
    mutate: revalidate,
  };
}

// Save DC filters for current user
export async function saveDCFilters(filters: DCFilters): Promise<{ filters: DCFilters }> {
  // Use the central api.ts helper that has the proper token getter
  const { apiPutAuth } = await import('./api');
  return apiPutAuth<{ filters: DCFilters }>('/dixon-coles/filters', { filters });
}

// Load saved DC filter presets for current user
// shouldFetch controls whether to make the request (should be true when user is signed in AND auth is ready)
export function useDCFilterPresets(shouldFetch: boolean = false) {
  const { data, error, mutate: revalidate } = useSWR<DCFilterPresetsResponse>(
    // Only fetch when shouldFetch is true (user is authenticated and SWR auth is ready)
    shouldFetch ? '/dixon-coles/presets' : null,
    fetcher,
    {
      revalidateOnFocus: false,
      dedupingInterval: 60000,
    }
  );

  return {
    presets: data?.presets || [],
    error,
    isLoading: shouldFetch ? (!data && !error) : false,
    mutate: revalidate,
  };
}

// Create a new DC filter preset
export async function createDCFilterPreset(
  name: string,
  filters: DCFilters
): Promise<DCFilterPreset> {
  // Use the central api.ts helper that has the proper token getter
  const { apiPostAuth } = await import('./api');
  return apiPostAuth<DCFilterPreset>('/dixon-coles/presets', { name, filters });
}

// Update an existing DC filter preset
export async function updateDCFilterPreset(
  presetId: string,
  updates: { name?: string; filters?: DCFilters }
): Promise<DCFilterPreset> {
  // Use the central api.ts helper that has the proper token getter
  const { apiPutAuth } = await import('./api');
  return apiPutAuth<DCFilterPreset>(`/dixon-coles/presets/${presetId}`, updates);
}

// Delete a DC filter preset
export async function deleteDCFilterPreset(presetId: string): Promise<void> {
  // Use the central api.ts helper that has the proper token getter
  const { apiDeleteAuth } = await import('./api');
  return apiDeleteAuth(`/dixon-coles/presets/${presetId}`);
}

// ============================================
// Dashboard Widget Hooks
// ============================================

// Dashboard layout hook
export function useDashboardLayout() {
  const { data, error, mutate: revalidate } = useSWR<{
    layouts: Record<string, Array<{ i: string; x: number; y: number; w: number; h: number }>>;
    widgets: Array<{ id: string; type: string; enabled: boolean; settings: Record<string, unknown> }>;
    version: number;
    updated_at: string;
  }>('/dashboard/layout', fetcher);

  return {
    data,
    error,
    isLoading: !data && !error,
    mutate: revalidate,
  };
}

// Save dashboard layout
export async function saveDashboardLayout(
  layouts: Record<string, Array<{ i: string; x: number; y: number; w: number; h: number }>>,
  widgets: Array<{ id: string; type: string; enabled: boolean; settings: Record<string, unknown> }>
) {
  const token = authTokenGetter ? await authTokenGetter() : null;
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const res = await fetch(`${API_BASE}/dashboard/layout`, {
    method: 'PUT',
    headers,
    body: JSON.stringify({ layouts, widgets }),
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || `Failed to save layout: ${res.status}`);
  }
  return res.json();
}

// Dixon-Coles top picks hook
export function useDCTopPicks(limit: number = 5, minEdge: number = 5) {
  const { data, error, mutate: revalidate } = useSWR<{
    picks: Array<{
      fixture_id: number;
      home_team: string;
      away_team: string;
      home_crest_url?: string;
      away_crest_url?: string;
      kickoff_utc: string;
      market: string;
      outcome: string;
      probability: number;
      implied_prob: number;
      edge: number;
      best_odds: number;
    }>;
  }>(
    `/dixon-coles/top-picks?limit=${limit}&min_edge=${minEdge}`,
    fetcher,
    { refreshInterval: 60000 }
  );

  return {
    data: data?.picks || [],
    error,
    isLoading: !data && !error,
    mutate: revalidate,
  };
}

// Trending teams hook
export function useTrendingTeams(limit: number = 6) {
  const { data, error, mutate: revalidate } = useSWR<{
    teams: Array<{
      team_id: number;
      name: string;
      crest_url?: string;
      trend_score: number;
      trend_direction: 'up' | 'down' | 'stable';
      reason: string;
      form: string;
    }>;
  }>(
    `/teams/trending?limit=${limit}`,
    fetcher,
    { refreshInterval: 120000 }
  );

  return {
    data: data?.teams || [],
    error,
    isLoading: !data && !error,
    mutate: revalidate,
  };
}

// Strategy alerts summary hook
export function useStrategyAlertsSummary() {
  // Custom fetcher that returns empty data on 401 (not logged in)
  const authAwareFetcher = async (url: string) => {
    try {
      return await fetcher(url);
    } catch (err: any) {
      // Return empty data if not authenticated
      if (err.message?.includes('401')) {
        return { alerts: [], total_strategies: 0, total_new_matches: 0 };
      }
      throw err;
    }
  };

  const { data, error, mutate: revalidate } = useSWR<{
    alerts: Array<{
      id: number;
      name: string;
      new_matches: number;
      total_matches: number;
      last_checked?: string;
    }>;
    total_strategies: number;
    total_new_matches: number;
  }>(
    '/strategies/alerts/summary',
    authAwareFetcher,
    { refreshInterval: 60000 }
  );

  return {
    data: data?.alerts || [],
    totalNewMatches: data?.total_new_matches || 0,
    error,
    isLoading: !data && !error,
    mutate: revalidate,
  };
}


// ============================================
// League Page Hooks
// ============================================

import type { LeagueOverviewOut, TeamOfWeekOut, TopPlayerOut, GameweekFixturesOut } from './types';

// League overview (combined data for overview tab)
export function useLeagueOverview(apiLeagueId: number | null, season?: string) {
  const params = new URLSearchParams();
  if (season) params.set('season', season);
  const query = params.toString();

  const { data, error, mutate: revalidate } = useSWR<LeagueOverviewOut>(
    apiLeagueId ? `/leagues/${apiLeagueId}${query ? `?${query}` : ''}` : null,
    fetcher,
    {
      refreshInterval: 300000, // 5 minutes
      revalidateOnFocus: false,
    }
  );

  return {
    data: data || null,
    error,
    isLoading: apiLeagueId !== null && !error && !data,
    mutate: revalidate,
  };
}

// League standings only
export function useLeagueStandings(apiLeagueId: number | null, season?: string) {
  const params = new URLSearchParams();
  if (season) params.set('season', season);
  const query = params.toString();

  const { data, error, mutate: revalidate } = useSWR(
    apiLeagueId ? `/leagues/${apiLeagueId}/standings${query ? `?${query}` : ''}` : null,
    fetcher,
    {
      refreshInterval: 300000,
      revalidateOnFocus: false,
    }
  );

  return {
    data: data || null,
    error,
    isLoading: apiLeagueId !== null && !error && !data,
    mutate: revalidate,
  };
}

// League top players
export function useLeagueTopPlayers(
  apiLeagueId: number | null,
  statType?: 'goals' | 'assists' | 'rating',
  season?: string,
  limit?: number
) {
  const params = new URLSearchParams();
  if (statType) params.set('stat_type', statType);
  if (season) params.set('season', season);
  if (limit) params.set('limit', String(limit));
  const query = params.toString();

  const { data, error, mutate: revalidate } = useSWR<{ players: TopPlayerOut[] }>(
    apiLeagueId ? `/leagues/${apiLeagueId}/top-players${query ? `?${query}` : ''}` : null,
    fetcher,
    {
      refreshInterval: 300000,
      revalidateOnFocus: false,
    }
  );

  return {
    data: data?.players || [],
    error,
    isLoading: apiLeagueId !== null && !error && !data,
    mutate: revalidate,
  };
}

// League team of the week
export function useLeagueTeamOfWeek(apiLeagueId: number | null, season?: string) {
  const params = new URLSearchParams();
  if (season) params.set('season', season);
  const query = params.toString();

  const { data, error, mutate: revalidate } = useSWR<TeamOfWeekOut | null>(
    apiLeagueId ? `/leagues/${apiLeagueId}/team-of-week${query ? `?${query}` : ''}` : null,
    fetcher,
    {
      refreshInterval: 300000,
      revalidateOnFocus: false,
    }
  );

  return {
    data: data || null,
    error,
    isLoading: apiLeagueId !== null && !error && !data,
    mutate: revalidate,
  };
}

// League next gameweek fixtures
export function useLeagueNextGameweek(apiLeagueId: number | null, season?: string) {
  const params = new URLSearchParams();
  if (season) params.set('season', season);
  const query = params.toString();

  const { data, error, mutate: revalidate } = useSWR<GameweekFixturesOut>(
    apiLeagueId ? `/leagues/${apiLeagueId}/next-gameweek${query ? `?${query}` : ''}` : null,
    fetcher,
    {
      refreshInterval: 300000,
      revalidateOnFocus: false,
    }
  );

  return {
    data: data || null,
    error,
    isLoading: apiLeagueId !== null && !error && !data,
    mutate: revalidate,
  };
}

// League all fixtures (for Fixtures tab)
export function useLeagueFixtures(
  apiLeagueId: number | null,
  params?: {
    status?: 'upcoming' | 'completed' | 'all';
    gameweek?: number;
    season?: string;
    limit?: number;
    offset?: number;
  }
) {
  const searchParams = new URLSearchParams();
  if (params?.status) searchParams.set('status', params.status);
  if (params?.gameweek) searchParams.set('gameweek', String(params.gameweek));
  if (params?.season) searchParams.set('season', params.season);
  if (params?.limit) searchParams.set('limit', String(params.limit));
  if (params?.offset) searchParams.set('offset', String(params.offset));
  const query = searchParams.toString();

  const { data, error, mutate: revalidate } = useSWR(
    apiLeagueId ? `/leagues/${apiLeagueId}/fixtures${query ? `?${query}` : ''}` : null,
    fetcher,
    {
      refreshInterval: 300000,
      revalidateOnFocus: false,
    }
  );

  return {
    data: data || null,
    error,
    isLoading: apiLeagueId !== null && !error && !data,
    mutate: revalidate,
  };
}


// Global SWR configuration
export function SwrProvider({ children }: SwrProviderProps) {
  return (
    <SWRConfig
      value={{
        // Revalidation settings
        revalidateOnFocus: false,
        revalidateOnReconnect: true,
        dedupingInterval: 2000,

        // Error retry settings
        errorRetryCount: 3,
        errorRetryInterval: 5000,
        shouldRetryOnError: true,

        // Cache settings
        focusThrottleInterval: 5000,

        // Default fetcher
        fetcher,
      }}
    >
      {children}
    </SWRConfig>
  );
}
